import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PlusCircle, X, Edit, Check, Trash2 } from "lucide-react";
import { TradingRules } from "../../types/tradingJournal";
import { useTradeJournal } from "../../context/TradeJournalContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";

const StrategyRules = () => {
  const {
    tradingRules,
    addTradingRules,
    updateTradingRules,
    deleteTradingRules,
  } = useTradeJournal();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<TradingRules | null>(null);
  const [setupName, setSetupName] = useState("");
  const [ruleName, setRuleName] = useState("");
  const [rules, setRules] = useState<string[]>([]);

  const handleAddRule = () => {
    if (ruleName.trim()) {
      setRules([...rules, ruleName.trim()]);
      setRuleName("");
    }
  };

  const handleRemoveRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const openNewRuleDialog = () => {
    setEditingRule(null);
    setSetupName("");
    setRules([]);
    setRuleName("");
    setDialogOpen(true);
  };

  const openEditRuleDialog = (rule: TradingRules) => {
    setEditingRule(rule);
    setSetupName(rule.setup);
    setRules([...rule.rules]);
    setRuleName("");
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (setupName.trim() && rules.length > 0) {
      if (editingRule) {
        updateTradingRules(editingRule.id, {
          name: setupName.trim(),
          setup: setupName.trim(),
          rules,
        });
      } else {
        await addTradingRules({
          name: setupName.trim(),
          setup: setupName.trim(),
          rules,
        });
      }
      setDialogOpen(false);
    }
  };

  const handleDelete = (id: string) => {
    deleteTradingRules(id);
  };

  return (
    <>
      <Card className="intellectia-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Strategy Rules Checklist</CardTitle>
          <Button
            onClick={openNewRuleDialog}
            className="intellectia-button text-black"
            size="sm"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Strategy
          </Button>
        </CardHeader>
        <CardContent>
          {tradingRules.length === 0 ? (
            <div className="text-center py-12 text-white/60">
              <p>No strategy rules defined yet.</p>
              <p className="mt-2">
                Create rules for each trading setup to track your rule
                adherence.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {tradingRules.map((rule) => (
                <Card key={rule.id} className="bg-white/5 border-white/10">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg">{rule.setup}</CardTitle>
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditRuleDialog(rule)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-400"
                          onClick={() => handleDelete(rule.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {rule.rules.map((item, index) => (
                        <li
                          key={index}
                          className="flex items-center space-x-2 text-sm"
                        >
                          <div className="h-5 w-5 rounded-full border border-white/20 flex items-center justify-center flex-shrink-0">
                            <Check className="h-3 w-3 text-white/60" />
                          </div>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="intellectia-card sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? "Edit Strategy Rules" : "Add Strategy Rules"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="setupName">Strategy/Setup Name</Label>
              <Input
                id="setupName"
                value={setupName}
                onChange={(e) => setSetupName(e.target.value)}
                placeholder="e.g. ORB, VWAP Retest, Breakout"
                className="bg-white/5 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rules">Rules Checklist</Label>
              <div className="flex space-x-2">
                <Input
                  id="rules"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="e.g. Risk-Reward ratio at least 2:1"
                  className="bg-white/5 border-white/10"
                  onKeyDown={(e) => e.key === "Enter" && handleAddRule()}
                />
                <Button
                  onClick={handleAddRule}
                  type="button"
                  variant="secondary"
                >
                  Add
                </Button>
              </div>
            </div>

            {rules.length > 0 && (
              <div className="border border-white/10 rounded-lg p-3 space-y-2">
                {rules.map((rule, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-white/5 p-2 rounded"
                  >
                    <span className="text-sm">{rule}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleRemoveRule(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="bg-white/5 border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="intellectia-button text-black"
            >
              {editingRule ? "Update" : "Save"} Rules
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StrategyRules;
